import { ElectronAPI } from '#~/electron/api.js';
import {
  isDesktopOnlyFeatureAvailable,
  reportDesktopOnlyFeatureGate,
  resolveFeatureGateClient
} from '#~/analytics/domains/feature-gate.js';
import {
  DESKTOP_ONLY_FEATURE,
  type EnvelopeClient
} from '#~/analytics/constants/event-properties.js';

// NetworkInfo contains the network interfaces (array of [interface, ip]) and an
// available port returned by the electron app.
export interface NetworkInfo {
  networks: { name: string; address: string }[];
  port: number;
}

/**
 * Requests the local machine IP and an available port from the Electron app.
 *
 * IP-controller management is desktop-only: the address/port come from an Electron
 * IPC that does not exist in the browser build. Before ENG-4332 the browser path fell
 * through to `ElectronAPI.getIPPort()` returning `undefined` and threw
 * "No network interfaces found" — an UNCAUGHT rejection (the only caller,
 * `useAddDevice.updateNetInfo`, does not catch), which posthog-js autocaptured as an
 * `$exception`. A browser user opening the controllers panel is not a defect, so the
 * browser path now records guidance and resolves with `undefined`.
 *
 * The Electron path is unchanged, INCLUDING the throw: on the desktop surface a
 * missing interface list IS a real failure and must keep surfacing as one.
 *
 * @param client The surface to gate on. Defaults to the resolved surface; callers in
 *   product code never pass it.
 */
export async function getNetworkInfo(
  client: EnvelopeClient = resolveFeatureGateClient()
): Promise<NetworkInfo | undefined> {
  if (!isDesktopOnlyFeatureAvailable(client)) {
    reportDesktopOnlyFeatureGate(DESKTOP_ONLY_FEATURE.IP_CONTROLLER_MANAGEMENT, client);
    return undefined;
  }

  const netInfo = await ElectronAPI.getIPPort();
  if (!netInfo || !netInfo.networks || !netInfo.port) {
    throw new Error('No network interfaces found');
  }
  return netInfo;
}
